import * as THREE from 'three'
import type { VisualParams } from '../../types'
import type { SharedUniforms } from '../AudioUniforms'
import { PRIMITIVE_TEXTURE_GLSL } from '../AudioUniforms'
import { BasePrimitive } from './Primitive'
import { hsv2rgb, snoise, POINT_SPRITE_GLSL, ZONE_GLSL_COMMON, ZONE_GLSL_FRAG } from '../shaderChunks'

/**
 * Infinite point terrain. A long plane in XZ whose relief (fbm noise +
 * spectrum) scrolls toward the camera over time: it appears to come from the
 * horizon and we travel through it. The distant rows fade out to create the
 * infinite effect.
 */
export class TerrainField extends BasePrimitive {
  object3D: THREE.Points
  private material: THREE.ShaderMaterial

  constructor(uniforms: SharedUniforms) {
    super(uniforms)
    this.material = new THREE.ShaderMaterial({
      uniforms: uniforms as unknown as Record<string, THREE.IUniform>,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexShader: /* glsl */ `
        attribute vec2 aGrid;      // (u,v) in 0..1
        uniform float uTime, uBass, uLevel, uBeat, uReactivity, uBassPunch, uPixelRatio, uPointScale;
        uniform vec3 uSeed;
        uniform float uSeedPulse;
        uniform sampler2D uFreq;
        varying float vH;
        varying float vFade;
        varying vec3 vDir;
        ${snoise}
        ${ZONE_GLSL_COMMON}
        void main() {
          // When zone reaction is on, the terrain is frozen — only the zones react.
          float gate = 1.0 - uZoneEnabled;
          float u = aGrid.x;
          float scroll = uTime * 4.5 * gate;     // travel speed (frozen in zone mode)
          float zc = position.z - scroll;        // flowing coordinate
          // 3-octave fbm for the relief.
          float n  = snoise(vec3(position.x * 0.16, zc * 0.16, 0.0)) * 1.0;
          n       += snoise(vec3(position.x * 0.37, zc * 0.37, 5.0)) * 0.5;
          n       += snoise(vec3(position.x * 0.80, zc * 0.80, 9.0)) * 0.25;
          // The spectrum accentuates the relief (center = lows).
          float mag = texture2D(uFreq, vec2(clamp(abs(u - 0.5) * 2.0, 0.0, 1.0), 0.5)).r;
          float h = (n + (mag * 1.3 + uBass * uBassPunch * 0.7 + uBeat * 0.4) * gate)
                  * (0.6 + uLevel * 1.1 * gate) * uReactivity;
          // Seed-driven beat punch (suppressed when zone reaction is on).
          float lobe = snoise(vec3(position.x * 0.5, zc * 0.5, 3.0) + uSeed);
          h += lobe * uSeedPulse * uReactivity * 0.8;
          // Zone reaction: shapes ride the grid (u,v) and punch the relief locally.
          vec3 zdir = zoneDirFromUV(aGrid);
          vDir = zdir;
          h += zoneDisp(zdir) * 1.4;
          // Always-on idle ripple so the terrain never looks frozen (e.g. in zone mode).
          h += (sin(position.x * 0.6 + uTime * 0.7) + sin(position.z * 0.5 - uTime * 0.6)) * 0.12;
          vH = h;
          vec3 pos = position;
          pos.y += h;
          vec4 mv = modelViewMatrix * vec4(pos, 1.0);
          gl_Position = projectionMatrix * mv;
          float dist = -mv.z;
          // Fade: appears far away and holds up close (horizon -> infinite).
          vFade = smoothstep(52.0, 30.0, dist) * smoothstep(0.5, 4.0, dist);
          gl_PointSize = clamp((3.0 + mag * 4.0 + h * 0.8) * (10.0 / dist), 1.2, 12.0) * uPixelRatio * uPointScale;
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        ${PRIMITIVE_TEXTURE_GLSL}
        uniform float uHue, uTreble;
        varying float vH;
        varying float vFade;
        varying vec3 vDir;
        ${hsv2rgb}
        ${POINT_SPRITE_GLSL}
        ${ZONE_GLSL_COMMON}
        ${ZONE_GLSL_FRAG}
        void main() {
          float core = pointMask();
          if (core < 0.01) discard;
          float hue = fract(uHue + vH * 0.10 + uTreble * 0.1);
          vec3 col = hsv2rgb(vec3(hue, 0.8, 0.45 + clamp(vH, 0.0, 1.5) * 0.45));
          float alpha = core * vFade * (uPointSolid > 0.5 ? 1.0 : (0.35 + clamp(vH, 0.0, 1.0) * 0.5));
          vec4 zc = zoneColor(vDir);
          vec3 outc = mix(applyPrimitiveTexture(col * core), zc.rgb * core, zc.a);
          gl_FragColor = vec4(outc, max(alpha, zc.a * core * vFade));
        }
      `,
    })
    this.object3D = new THREE.Points(new THREE.BufferGeometry(), this.material)
    this.object3D.frustumCulled = false
    // Lower the plane so we look "across" it toward the horizon.
    this.object3D.position.y = -1.6
    this.rebuild({ density: 1 } as VisualParams)
  }

  update(params: VisualParams): void {
    const solid = params.pointSolid === true
    this.material.blending = solid ? THREE.NormalBlending : THREE.AdditiveBlending
    this.material.depthWrite = solid
  }

  rebuild(params: VisualParams): void {
    const d = params.density ?? 1
    const cols = Math.floor(190 * Math.sqrt(d))
    const rows = Math.floor(150 * Math.sqrt(d))
    const width = 18
    const depthNear = 6 // near the camera (z+)
    const depthFar = -50 // toward the horizon (z-)
    const count = cols * rows
    const positions = new Float32Array(count * 3)
    const grid = new Float32Array(count * 2)

    let i = 0
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const u = c / (cols - 1)
        const v = r / (rows - 1)
        positions[i * 3 + 0] = (u - 0.5) * width
        positions[i * 3 + 1] = 0
        positions[i * 3 + 2] = depthNear + v * (depthFar - depthNear)
        grid[i * 2 + 0] = u
        grid[i * 2 + 1] = v
        i++
      }
    }
    const geo = this.object3D.geometry
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geo.setAttribute('aGrid', new THREE.BufferAttribute(grid, 2))
    geo.computeBoundingSphere()
  }
}
